import React, { useRef } from "react";
import { Typography, Button, Input, Stack } from "@altimateai/lego";

interface LangfuseDateFiltersProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onPresetClick: (days: number) => void;
  onClearClick: () => void;
}

export const LangfuseDateFilters: React.FC<LangfuseDateFiltersProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onPresetClick,
  onClearClick,
}) => {
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const isPresetActive = (days: number): boolean => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    const expectedStart = start.toISOString().split("T")[0];
    const expectedEnd = end.toISOString().split("T")[0];
    return startDate === expectedStart && endDate === expectedEnd;
  };

  const showClearButton = !isPresetActive(7) && !isPresetActive(30);

  const handleStartDateChange = (date: string) => {
    const start = new Date(date);
    const end = new Date(endDate);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 31) {
      const maxStart = new Date(end);
      maxStart.setDate(maxStart.getDate() - 31);
      onStartDateChange(maxStart.toISOString().split("T")[0]);
    } else {
      onStartDateChange(date);
    }
  };

  const handleEndDateChange = (date: string) => {
    const start = new Date(startDate);
    const end = new Date(date);
    const diffDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 31) {
      const maxEnd = new Date(start);
      maxEnd.setDate(maxEnd.getDate() + 31);
      onEndDateChange(maxEnd.toISOString().split("T")[0]);
    } else {
      onEndDateChange(date);
    }
  };

  return (
    <Stack direction="row" gap="md" align="center" className="al-flex-wrap">
      {showClearButton && (
        <Button variant="ghost" size="sm" onClick={onClearClick}>
          Clear
        </Button>
      )}

      <Stack direction="row" gap="xs">
        <Button
          variant={isPresetActive(7) ? "default" : "outline"}
          size="sm"
          onClick={() => onPresetClick(7)}
        >
          Last 7 Days
        </Button>
        <Button
          variant={isPresetActive(30) ? "default" : "outline"}
          size="sm"
          onClick={() => onPresetClick(30)}
        >
          Last 30 Days
        </Button>
      </Stack>

      <Stack direction="row" align="center" gap="xs">
        <Input
          ref={startDateRef}
          type="date"
          value={startDate}
          onChange={e => handleStartDateChange(e.target.value)}
          onClick={() => startDateRef.current?.showPicker()}
          onKeyDown={e => e.preventDefault()}
          className="al-w-36 al-cursor-pointer"
        />
        <Typography variant="caption" className="al-text-gray-600">
          to
        </Typography>
        <Input
          ref={endDateRef}
          type="date"
          value={endDate}
          onChange={e => handleEndDateChange(e.target.value)}
          onClick={() => endDateRef.current?.showPicker()}
          onKeyDown={e => e.preventDefault()}
          className="al-w-36 al-cursor-pointer"
        />
      </Stack>
    </Stack>
  );
};
